import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t } from '../i18n.js';
import { api } from '../state/auth.js';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { useAuth } from '../hooks/useAuth.js';
import { fetchFeatures } from '../utils/features.js';

const PERMISSION_RESOURCES = [
  { key: 'processes',     label: 'Processen',               actions: ['read', 'create', 'update', 'delete'] },
  { key: 'tasks',         label: 'Taken',                   actions: ['read', 'create', 'update', 'delete'] },
  { key: 'templates',     label: 'Sjablonen',               actions: ['read', 'create', 'update', 'delete'] },
  { key: 'users',         label: 'Gebruikers',              actions: ['read', 'create', 'update', 'delete'] },
  { key: 'documents',     label: 'Documenten',              actions: ['read', 'create', 'update', 'delete'] },
  { key: 'policies',      label: 'Beleidsbibliotheek',      actions: ['read', 'create', 'update', 'delete'] },
  { key: 'news',          label: 'Nieuws',                  actions: ['read', 'create', 'update', 'delete'],
    hint: 'Aanmaken of bewerken = redactie: voor iedereen en elke groep. Met alleen lezen post een lid voor de eigen groepen.' },
  { key: 'seasons',       label: 'Seizoenbeheer',           actions: ['read', 'create', 'update', 'delete'] },
  { key: 'recurring',     label: 'Terugkerende processen',  actions: ['read', 'create', 'update', 'delete'] },
  { key: 'coins',         label: 'Coins',                   actions: ['read', 'create'] },
  { key: 'calendar',      label: 'Kalender (bekijken)',      actions: ['read'] },
  { key: 'calendar_manage', label: 'Kalender (events beheren)', actions: ['create', 'update', 'delete'] },
  { key: 'calendar_admin',  label: 'Kalender (beheerder)',   actions: ['read'] },
  { key: 'reports',       label: 'Rapporten',               actions: ['read'] },
  { key: 'audit',         label: 'Auditlog',                actions: ['read'] },
  { key: 'settings',      label: 'Instellingen',            actions: ['read', 'update'] },
  { key: 'notifications', label: 'Meldingen',               actions: ['read'] },
  { key: 'certification', label: 'K&P Certificering',       actions: ['read', 'create', 'update', 'delete'] },
  { key: 'kwalificaties', label: 'Kwalificaties',           actions: ['read', 'create', 'update', 'delete'] },
  { key: 'meetings',      label: 'Vergaderingen (alle gremia)', actions: ['read', 'create', 'update', 'delete'] },
  { key: 'volunteers',    label: 'Vrijwilligers (klussenbord)', actions: ['read', 'create', 'update', 'delete'] },
  // Lezen: in het menu en op het dashboard (openen via een link kan elk lid);
  // Bewerken: doelen aanpassen / Clubtrots beheren
  { key: 'goals',         label: 'Visie & doelen (lezen = in menu)', actions: ['read', 'update'], feature: 'goals' },
  { key: 'pride',         label: 'Clubtrots (lezen = in menu)', actions: ['read', 'update'], feature: 'pride' },
  // Lezen: de ✨-schrijfhulp bij tekstvelden (als de club een AI-sleutel heeft ingesteld)
  { key: 'ai',            label: 'AI-schrijfhulp (lezen = gebruiken)', actions: ['read'] },
];

// Permissions of an unreleased feature (services/features.js) stay out of sight
const shownResources = features => PERMISSION_RESOURCES.filter(r => !r.feature || features?.[r.feature]);

const ALL_ACTIONS = ['read', 'create', 'update', 'delete'];
const ACTION_LABELS = { read: 'Lezen', create: 'Aanmaken', update: 'Bewerken', delete: 'Verwijderen' };

function emptyPermissions() {
  const p = {};
  for (const r of PERMISSION_RESOURCES) p[r.key] = [];
  return p;
}

function parsePermissions(json) {
  try {
    const raw = typeof json === 'string' ? JSON.parse(json) : (json || {});
    const p = emptyPermissions();
    for (const r of PERMISSION_RESOURCES) {
      if (Array.isArray(raw[r.key])) p[r.key] = raw[r.key].filter(a => r.actions.includes(a));
    }
    return p;
  } catch {
    return emptyPermissions();
  }
}

// Compact inline permissions preview (read-only chips)
function PermissionsBadges({ permissions, features }) {
  const perms = parsePermissions(permissions);
  const active = shownResources(features).filter(r => perms[r.key]?.length > 0);
  if (active.length === 0) return <span style="font-size:12px;color:var(--color-text-muted)">Geen permissies</span>;
  return (
    <div style="display:flex;flex-wrap:wrap;gap:6px">
      {active.map(r => (
        <div key={r.key} style="display:flex;align-items:center;gap:4px;background:var(--color-bg-secondary);border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;font-size:12px">
          <span style="font-weight:600">{r.label}</span>
          <span style="color:var(--color-text-muted)">·</span>
          <span style="color:var(--color-text-muted)">{perms[r.key].map(a => ACTION_LABELS[a]).join(', ')}</span>
        </div>
      ))}
    </div>
  );
}

// Full permissions editor for the modal
function PermissionsEditor({ permissions, onChange, features }) {
  const resources = shownResources(features);
  const toggle = (resource, action) => {
    const current = permissions[resource] || [];
    const next = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
    onChange({ ...permissions, [resource]: next });
  };

  const toggleRow = (resource) => {
    const res = resources.find(r => r.key === resource);
    const current = permissions[resource] || [];
    const allSelected = res.actions.every(a => current.includes(a));
    onChange({ ...permissions, [resource]: allSelected ? [] : [...res.actions] });
  };

  const toggleCol = (action) => {
    const updated = { ...permissions };
    const columnResources = resources.filter(r => r.actions.includes(action));
    const allSelected = columnResources.every(r => (updated[r.key] || []).includes(action));
    for (const r of columnResources) {
      if (allSelected) updated[r.key] = (updated[r.key] || []).filter(a => a !== action);
      else if (!updated[r.key].includes(action)) updated[r.key] = [...(updated[r.key] || []), action];
    }
    onChange(updated);
  };

  return (
    <div style="border:1px solid var(--color-border);border-radius:8px;overflow:hidden">
      {/* Header row */}
      <div style="display:grid;grid-template-columns:140px repeat(4,1fr) 44px;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border)">
        <div style="padding:8px 12px;font-size:12px;font-weight:600;color:var(--color-text-muted)">Module</div>
        {ALL_ACTIONS.map(action => (
          <div key={action} style="padding:8px 4px;text-align:center;font-size:12px;font-weight:600;color:var(--color-text-muted)">
            <div>{ACTION_LABELS[action]}</div>
            <input
              type="checkbox"
              style="margin-top:4px;cursor:pointer"
              onChange={() => toggleCol(action)}
              checked={resources.filter(r => r.actions.includes(action)).every(r => (permissions[r.key] || []).includes(action))}
            />
          </div>
        ))}
        <div style="padding:8px 4px;text-align:center;font-size:12px;font-weight:600;color:var(--color-text-muted)">Alles</div>
      </div>

      {/* Data rows */}
      {resources.map((res, idx) => {
        const current = permissions[res.key] || [];
        const allSelected = res.actions.every(a => current.includes(a));
        return (
          <div
            key={res.key}
            style={`display:grid;grid-template-columns:140px repeat(4,1fr) 44px;${idx < resources.length - 1 ? 'border-bottom:1px solid var(--color-border)' : ''}${idx % 2 ? ';background:var(--color-bg-secondary)' : ''}`}
          >
            <div style="padding:8px 12px;font-size:13px;font-weight:500;display:flex;flex-direction:column;justify-content:center" title={res.hint || undefined}>
              {res.label}
              {res.hint && <span style="font-size:11px;font-weight:400;color:var(--color-text-muted);line-height:1.35;margin-top:2px">{res.hint}</span>}
            </div>
            {ALL_ACTIONS.map(action => (
              <div key={action} style="display:flex;align-items:center;justify-content:center;padding:6px 4px">
                {res.actions.includes(action) ? (
                  <input
                    type="checkbox"
                    checked={current.includes(action)}
                    onChange={() => toggle(res.key, action)}
                    style="width:15px;height:15px;cursor:pointer;accent-color:var(--color-primary)"
                  />
                ) : (
                  <span style="color:var(--color-border);font-size:16px">—</span>
                )}
              </div>
            ))}
            <div style="display:flex;align-items:center;justify-content:center;padding:6px 4px">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => toggleRow(res.key)}
                style="width:15px;height:15px;cursor:pointer;accent-color:var(--color-primary)"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function RolesView() {
  const auth = useAuth();
  const [features, setFeatures] = useState({});
  useEffect(() => { fetchFeatures(`${auth.userId}:${auth.activeClubId}`).then(setFeatures); }, [auth.userId, auth.activeClubId]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [roleModal, setRoleModal] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPermissions, setFormPermissions] = useState(emptyPermissions());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRoles = () => {
    setLoading(true);
    api('/api/roles')
      .then(setRoles)
      .catch(() => setError('Kan rollen niet laden.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRoles(); }, []);

  const openCreate = () => {
    setFormName('');
    setFormDescription('');
    setFormPermissions(emptyPermissions());
    setFormError(null);
    setRoleModal({ mode: 'create' });
  };

  const openEdit = (role, e) => {
    e?.stopPropagation();
    setFormName(role.name);
    setFormDescription(role.description || '');
    setFormPermissions(parsePermissions(role.permissions_json));
    setFormError(null);
    setRoleModal({ mode: 'edit', role });
  };

  const openClone = (role, e) => {
    e?.stopPropagation();
    setFormName(`Kopie van ${role.name}`);
    setFormDescription(role.description || '');
    setFormPermissions(parsePermissions(role.permissions_json));
    setFormError(null);
    setRoleModal({ mode: 'create' });
  };

  const handleSave = async () => {
    if (!formName.trim()) { setFormError('Naam is verplicht'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (roleModal.mode === 'create') {
        await api('/api/roles', { method: 'POST', body: JSON.stringify({ name: formName, description: formDescription, permissions: formPermissions }) });
      } else {
        await api(`/api/roles/${roleModal.role.id}`, { method: 'PUT', body: JSON.stringify({ name: formName, description: formDescription, permissions: formPermissions }) });
      }
      setRoleModal(null);
      fetchRoles();
    } catch (err) {
      setFormError(err.code === 'name_exists' ? 'Deze naam bestaat al.' : 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api(`/api/roles/${confirmDelete.id}`, { method: 'DELETE' });
      setConfirmDelete(null);
      fetchRoles();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">Rollen</h1>
        <Button variant="primary" onClick={openCreate}>+ Rol aanmaken</Button>
      </div>

      {error && <div class="alert alert-error mt-4">{error}</div>}

      <div class="card mt-4" style="overflow:hidden">
        {/* Table header */}
        <div style="display:grid;grid-template-columns:1fr auto auto auto auto;gap:12px;padding:10px 20px;background:var(--color-bg-secondary);border-bottom:1px solid var(--color-border);font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;align-items:center">
          <span>Rolnaam</span>
          <span style="min-width:60px;text-align:center">Leden</span>
          <span style="min-width:80px"></span>
          <span style="min-width:80px"></span>
          <span style="min-width:80px"></span>
        </div>

        {loading ? (
          <div style="padding:16px;display:flex;flex-direction:column;gap:0">
            {[1,2,3,4,5].map(i => (
              <div key={i} style="padding:14px 20px;border-bottom:1px solid var(--color-border);display:flex;gap:16px;align-items:center">
                <div class="skeleton" style="flex:1;height:14px;border-radius:4px" />
                <div class="skeleton" style="width:40px;height:14px;border-radius:4px" />
                <div class="skeleton" style="width:80px;height:32px;border-radius:6px" />
                <div class="skeleton" style="width:80px;height:32px;border-radius:6px" />
              </div>
            ))}
          </div>
        ) : roles.length === 0 ? (
          <div style="padding:60px;text-align:center;color:var(--color-text-muted)">
            <div style="font-size:32px;margin-bottom:12px">🔑</div>
            <p>Nog geen rollen aangemaakt.</p>
          </div>
        ) : (
          roles.map((role, idx) => {
            const isExpanded = expandedId === role.id;
            const isLast = idx === roles.length - 1;
            return (
              <div key={role.id} style={`${!isLast || isExpanded ? 'border-bottom:1px solid var(--color-border)' : ''}`}>
                {/* Row */}
                <div
                  style="display:grid;grid-template-columns:1fr auto auto auto auto;gap:12px;padding:12px 20px;align-items:center;cursor:pointer;transition:background .15s"
                  class="list-item-row"
                  onClick={() => setExpandedId(isExpanded ? null : role.id)}
                >
                  <div>
                    <div style="font-size:14px;font-weight:600">{role.name}</div>
                    {role.description && (
                      <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px">{role.description}</div>
                    )}
                  </div>
                  <div style="min-width:60px;text-align:center">
                    <span style="font-size:13px;background:var(--color-bg-secondary);border:1px solid var(--color-border);padding:2px 10px;border-radius:12px;color:var(--color-text-muted)">
                      {role.member_count}
                    </span>
                  </div>
                  <Button variant="secondary" size="sm" style="min-width:80px" onClick={(e) => openEdit(role, e)}>
                    Bewerken
                  </Button>
                  <Button variant="secondary" size="sm" style="min-width:80px" onClick={(e) => openClone(role, e)}>
                    Klonen
                  </Button>
                  <Button variant="danger" size="sm" style="min-width:80px" onClick={(e) => { e.stopPropagation(); setConfirmDelete(role); }}>
                    Verwijderen
                  </Button>
                </div>

                {/* Expanded permissions */}
                {isExpanded && (
                  <div style="padding:16px 20px 20px;background:var(--color-bg-secondary);border-top:1px solid var(--color-border)">
                    <div style="font-size:12px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:10px">
                      Permissies
                    </div>
                    <PermissionsBadges permissions={role.permissions_json} features={features} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create/Edit modal */}
      <Modal
        isOpen={!!roleModal}
        title={roleModal?.mode === 'create' ? 'Nieuwe rol aanmaken' : `Bewerken: ${roleModal?.role?.name}`}
        onClose={() => setRoleModal(null)}
        style="max-width:700px;width:96vw"
      >
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px">
          <div class="form-group" style="margin-bottom:0">
            <label class="form-label">Naam *</label>
            <input
              class="form-input"
              type="text"
              value={formName}
              onInput={e => setFormName(e.target.value)}
              placeholder="bijv. Secretaris"
              autoFocus
            />
          </div>
          <div class="form-group" style="margin-bottom:0">
            <label class="form-label">Omschrijving</label>
            <input
              class="form-input"
              type="text"
              value={formDescription}
              onInput={e => setFormDescription(e.target.value)}
              placeholder="Optionele beschrijving"
            />
          </div>
        </div>

        <label class="form-label" style="margin-bottom:8px;display:block">Permissies</label>
        <PermissionsEditor permissions={formPermissions} onChange={setFormPermissions} features={features} />

        {formError && <div class="alert alert-error mt-3">{formError}</div>}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setRoleModal(null)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={!!confirmDelete} title="Rol verwijderen" onClose={() => setConfirmDelete(null)}>
        <p style="margin-bottom:12px;color:var(--color-text-muted)">
          Weet je zeker dat je rol <strong>{confirmDelete?.name}</strong> wilt verwijderen?
        </p>
        {confirmDelete?.member_count > 0 && (
          <div class="alert alert-error mb-3" style="font-size:13px">
            Let op: {confirmDelete.member_count} gebruiker{confirmDelete.member_count !== 1 ? 's hebben' : ' heeft'} deze rol — de koppeling wordt ook verwijderd.
          </div>
        )}
        <div class="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? t('common.loading') : t('common.delete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
